<template>
  <div
    :class="[
      'relative overflow-hidden border',
      hasPreview ? 'border-border bg-black' : wrapperEmptyClass,
    ]"
    style="border-radius: var(--radius); aspect-ratio: 3/4"
    @click="onWrapperClick"
  >
    <!-- Filled: backend image via AuthorizedImage (visible OR revealed by admin) -->
    <AuthorizedImage
      v-if="(canSeePhoto || revealed) && image?.object_name"
      :object-name="image.object_name"
      class="h-full w-full object-cover"
      @loaded="$emit('photo-loaded', image?.object_name || '')"
      @load-failed="$emit('photo-failed', image?.object_name || '')"
    />
    <!-- Filled: local preview (blob URL) -->
    <img
      v-else-if="previewSrc"
      :src="previewSrc"
      :alt="emptyLabel || 'photo'"
      class="h-full w-full object-cover"
    />
    <!-- Masked for privacy; clickable if revealable -->
    <button
      v-else-if="image?.object_name && !canSeePhoto"
      type="button"
      :disabled="!revealable"
      :class="[
        'relative flex h-full w-full items-center justify-center bg-muted/30 masked-stripes',
        revealable
          ? 'cursor-pointer transition-colors hover:bg-muted/60'
          : 'cursor-default',
      ]"
      :title="
        revealable ? $t('Показать фото') : $t('Фото скрыто для приватности')
      "
      @click.stop="handleReveal"
    >
      <span
        v-if="revealable"
        class="flex flex-col items-center gap-1 text-muted-foreground"
      >
        <svg-icon name="huge-eye" class="h-5 w-5" />
        <span class="text-[11px] font-medium">
          {{ $t("Показать фото") }}
        </span>
      </span>
    </button>
    <!-- Empty upload slot -->
    <div
      v-else
      class="flex h-full w-full flex-col items-center justify-center gap-1.5 text-center text-muted-foreground"
    >
      <svg-icon name="huge-add-image" class="h-5 w-5" />
      <span v-if="emptyLabel" class="text-[11px] font-medium">
        {{ emptyLabel }}
      </span>
      <span v-if="emptyHint" class="text-[10px] text-muted-foreground/70">
        {{ emptyHint }}
      </span>
      <span
        v-if="required"
        class="absolute bottom-1.5 left-1.5 text-[10px] text-red-500"
      >
        ★
      </span>
    </div>

    <!-- Scanner corner decoration (only on filled + visible photo) -->
    <svg
      v-if="hasVisiblePhoto"
      viewBox="0 0 120 160"
      preserveAspectRatio="none"
      class="pointer-events-none absolute inset-0 h-full w-full opacity-60"
    >
      <g stroke="#ffffff" stroke-width="1.5" fill="none" stroke-linecap="round">
        <path d="M8 20 L8 8 L20 8" />
        <path d="M100 8 L112 8 L112 20" />
        <path d="M8 140 L8 152 L20 152" />
        <path d="M100 152 L112 152 L112 140" />
      </g>
    </svg>

    <!-- Number label -->
    <span
      v-if="number !== null"
      class="absolute bottom-1.5 left-1.5 rounded px-1.5 py-0.5 text-[10px] font-semibold"
      :class="
        hasVisiblePhoto
          ? 'bg-black/60 text-white'
          : 'bg-muted text-muted-foreground'
      "
    >
      {{ size === "lg" ? $t("Фото") + " " : "" }}{{ paddedNumber }}
    </span>

    <!-- Top-right actions: hide button + action slot in one row -->
    <div class="absolute right-1.5 top-1.5 z-[5] flex items-center gap-1">
      <button
        v-if="revealed && !canSeePhoto"
        type="button"
        :title="$t('Скрыть фото')"
        class="inline-flex h-7 w-7 items-center justify-center rounded-md bg-black/60 text-white transition-colors hover:bg-black/80"
        @click.stop="revealed = false"
      >
        <svg-icon name="huge-close-eye" class="h-3.5 w-3.5" />
      </button>
      <slot name="actions" />
    </div>

    <!-- Check mark when filled via preview (upload flow) -->
    <span
      v-if="previewSrc && showCheckmark"
      class="absolute bottom-1.5 right-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-green-500 text-white"
    >
      <svg-icon name="huge-check" class="h-2.5 w-2.5" />
    </span>

    <!-- Loading overlay (per-cell) -->
    <div
      v-if="loading"
      class="absolute inset-0 z-10 flex items-center justify-center bg-black/40"
    >
      <Spinner size="sm" class="text-white" />
    </div>
  </div>
</template>

<script>
import AuthorizedImage from "./AuthorizedImage.vue";
import Spinner from "@/shared/ui/spinner/Spinner.vue";

export default {
  name: "FacePhotoCell",
  components: { AuthorizedImage, Spinner },
  props: {
    image: { type: Object, default: null },
    previewSrc: { type: String, default: "" },
    number: { type: Number, default: null },
    size: { type: String, default: "md" },
    loading: { type: Boolean, default: false },
    canSeePhoto: { type: Boolean, default: true },
    revealable: { type: Boolean, default: false },
    emptyLabel: { type: String, default: "" },
    emptyHint: { type: String, default: "" },
    required: { type: Boolean, default: false },
    clickable: { type: Boolean, default: false },
    showCheckmark: { type: Boolean, default: false },
  },
  emits: ["click-empty", "reveal", "photo-loaded", "photo-failed"],
  data() {
    return {
      revealed: false,
    };
  },
  computed: {
    hasPreview() {
      return !!(this.previewSrc || this.image?.object_name);
    },
    hasVisiblePhoto() {
      if (this.previewSrc) return true;
      if (this.image?.object_name) return this.canSeePhoto || this.revealed;
      return false;
    },
    paddedNumber() {
      if (this.number === null) return "";
      return String(this.number).padStart(2, "0");
    },
    wrapperEmptyClass() {
      return this.clickable
        ? "border-[1.5px] border-dashed border-border bg-muted/40 cursor-pointer transition-colors hover:border-foreground/40 hover:bg-muted"
        : "border-border bg-muted/40";
    },
  },
  watch: {
    "image.object_name"() {
      this.revealed = false;
    },
    canSeePhoto() {
      if (this.canSeePhoto) this.revealed = false;
    },
    revealable(val) {
      if (!val) this.revealed = false;
    },
  },
  methods: {
    onWrapperClick() {
      if (this.hasPreview) return;
      if (!this.clickable) return;
      this.$emit("click-empty");
    },
    handleReveal() {
      if (!this.revealable) return;
      this.revealed = true;
      this.$emit("reveal", this.image?.object_name || "");
    },
  },
};
</script>

<style scoped>
.masked-stripes {
  background-image: repeating-linear-gradient(
    135deg,
    hsl(var(--muted)) 0 10px,
    hsl(var(--background)) 10px 20px
  );
  background-size: 28px 28px;
}
</style>
